import { AuthError, User, Session } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

export interface SupabaseUser {
  id: string;
  uid: string; // Alias pour compatibilité Firebase
  email: string | undefined;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

// Convertir User Supabase en format compatible
const mapSupabaseUser = (user: User): SupabaseUser => ({
  id: user.id,
  uid: user.id, // Alias pour compatibilité Firebase
  email: user.email,
  displayName: user.user_metadata?.full_name || user.user_metadata?.name || null,
  photoURL: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
  // Use !! to handle both null and undefined (Supabase returns undefined after signup)
  emailVerified: !!user.email_confirmed_at,
});

export class SupabaseAuthService {
  /**
   * Inscription avec email/mot de passe
   */
  async register(email: string, password: string): Promise<SupabaseUser> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        throw this.handleAuthError(error);
      }

      if (!data.user) {
        throw new Error('Erreur lors de l\'inscription');
      }

      // Note: Le profil organisateur est créé automatiquement par le trigger
      // PostgreSQL 'on_auth_user_created_organizer' (voir migration 037)

      return mapSupabaseUser(data.user);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur lors de l\'inscription');
    }
  }

  /**
   * Connexion avec email/mot de passe
   */
  async login(email: string, password: string): Promise<SupabaseUser> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw this.handleAuthError(error);
      }

      if (!data.user) {
        throw new Error('Erreur lors de la connexion');
      }

      return mapSupabaseUser(data.user);
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      throw error;
    }
  }

  /**
   * Connexion avec Google OAuth
   */
  async signInWithGoogle(): Promise<void> {
    try {
      const redirectUrl = `${window.location.origin}/callback`;
      console.log('🔐 [Auth] Google OAuth redirect URL:', redirectUrl);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });

      if (error) {
        throw this.handleAuthError(error);
      }

      // La redirection sera gérée par Supabase
      // Le callback traitera la création du profil si nécessaire
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur lors de la connexion avec Google');
    }
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      throw error;
    }
  }

  /**
   * Réinitialisation du mot de passe
   */
  async resetPassword(email: string): Promise<void> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw this.handleAuthError(error);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur lors de l\'envoi de l\'email de réinitialisation');
    }
  }

  /**
   * Resend verification email for unconfirmed accounts
   */
  async resendVerificationEmail(email: string): Promise<void> {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) {
        throw this.handleAuthError(error);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to resend verification email');
    }
  }

  /**
   * Écouter les changements d'état d'authentification
   */
  onAuthStateChanged(callback: (user: SupabaseUser | null) => void) {
    console.log('🔐 [AuthService] Configuration onAuthStateChange listener...');

    return supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 [AuthService] >>> CALLBACK APPELÉ! event:', event, 'session:', session ? 'présente' : 'null');

      if (session?.user) {
        const mappedUser = mapSupabaseUser(session.user);
        console.log('🔐 [AuthService] User connecté:', mappedUser.email);

        // Si c'est une nouvelle connexion OAuth, créer le profil si nécessaire
        if (event === 'SIGNED_IN') {
          console.log('🔐 [AuthService] SIGNED_IN - création profil si nécessaire...');
          await this.ensureOrganizerProfile(session.user);
        }

        callback(mappedUser);
      } else {
        console.log('🔐 [AuthService] Pas de session, callback(null)');
        callback(null);
      }
    });
  }

  /**
   * Obtenir la session actuelle
   */
  async getSession(): Promise<Session | null> {
    console.log('🔐 [AuthService] getSession() appelé...');
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('🔐 [AuthService] getSession error:', error);
        return null;
      }
      console.log('🔐 [AuthService] getSession result:', data.session ? 'session trouvée' : 'pas de session');
      return data.session;
    } catch (err) {
      console.error('🔐 [AuthService] getSession exception:', err);
      throw err;
    }
  }

  /**
   * Obtenir l'utilisateur actuel
   */
  async getCurrentUser(): Promise<SupabaseUser | null> {
    const { data } = await supabase.auth.getUser();
    return data.user ? mapSupabaseUser(data.user) : null;
  }

  /**
   * Créer le profil organisateur initial
   */
  private async createOrganizerProfile(user: User, email: string): Promise<void> {
    try {
      // 🔧 Fallback: utiliser le préfixe email si pas de full_name
      // Permet d'avoir un nom d'affichage même si le profil n'est pas encore rempli
      const defaultName = email.split('@')[0];

      // Check if email is already verified (OAuth users are pre-verified)
      const isEmailVerified = !!user.email_confirmed_at ||
        user.app_metadata?.provider === 'google';

      const organizerData = {
        id: user.id,
        organizer: user.user_metadata?.full_name || defaultName,
        organizer_contact: email,
        location: '',
        location_name: '',
        stats: {
          totalEvents: 0,
          totalViews: 0,
          activeEvents: 0,
        },
        // PRD-auth-email-verification.md: Track email verification on profile
        email_verified: isEmailVerified,
      };

      console.log('🔐 [Auth] Création profil organisateur pour:', email, 'ID:', user.id);

      // Using type assertion for email_verified column not yet in generated types
      const { error } = await supabase
        .from('organizers')
        .insert(organizerData as any);

      if (error) {
        if (error.code === '23505') {
          // 23505 = duplicate key, le profil existe déjà
          console.log('✅ [Auth] Profil organisateur existe déjà (OK)');
        } else {
          console.error('❌ [Auth] Erreur création profil organisateur:', error.code, error.message);
        }
      } else {
        console.log('✅ [Auth] Profil organisateur créé avec succès !');
      }
    } catch (error) {
      console.error('❌ [Auth] Exception création profil organisateur:', error);
    }
  }

  /**
   * S'assurer que le profil organisateur existe (pour OAuth, email confirmé, etc.)
   * Also syncs email_verified status (PRD-auth-email-verification.md)
   */
  async ensureOrganizerProfile(user: User): Promise<void> {
    try {
      // Vérifier si le profil existe
      // Using type assertion for email_verified column not yet in generated types
      const { data, error: selectError } = await supabase
        .from('organizers')
        .select('id, email_verified')
        .eq('id', user.id)
        .single();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existingProfile = data as any;

      if (selectError && selectError.code !== 'PGRST116') {
        // PGRST116 = "no rows returned", c'est normal si le profil n'existe pas
        console.warn('⚠️ [Auth] Erreur vérification profil:', selectError.message);
      }

      if (!existingProfile) {
        console.log('🔐 [Auth] Profil organisateur non trouvé, création...');
        await this.createOrganizerProfile(user, user.email || '');
      } else {
        console.log('✅ [Auth] Profil organisateur existant pour:', user.email);

        // PRD-auth-email-verification.md: Sync email_verified status
        // If user's email is now confirmed but profile doesn't reflect it, update it
        const isEmailNowVerified = !!user.email_confirmed_at ||
          user.app_metadata?.provider === 'google';

        if (isEmailNowVerified && !existingProfile.email_verified) {
          console.log('📧 [Auth] Syncing email_verified status to true for:', user.email);
          await supabase
            .from('organizers')
            .update({
              email_verified: true,
              updated_at: new Date().toISOString(),
            } as any)
            .eq('id', user.id);
        }
      }
    } catch (error) {
      // Le profil n'existe pas, le créer
      console.log('🔐 [Auth] Exception lors vérification, tentative création profil...');
      await this.createOrganizerProfile(user, user.email || '');
    }
  }

  /**
   * Gérer les erreurs d'authentification Supabase
   */
  private handleAuthError(error: AuthError): Error {
    // Check for rate limit (429) - Supabase message usually contains "security purposes" or time info
    if (error.message?.includes('security purposes') ||
        error.message?.includes('after') ||
        (error as any).status === 429) {
      const rateLimitError = new Error('Too many requests. Please wait before trying again.');
      (rateLimitError as any).isRateLimit = true;
      return rateLimitError;
    }

    switch (error.message) {
      case 'User already registered':
        return new Error('This email address is already registered');
      case 'Invalid login credentials':
        return new Error('Invalid email or password');
      case 'Email not confirmed':
        return new Error('Please confirm your email before signing in. Check your inbox (including spam folder).');
      case 'Password should be at least 6 characters':
      case 'Password should be at least 9 characters':
        return new Error('Password must be at least 9 characters with uppercase, lowercase and special character');
      default:
        // Handle password validation messages from Supabase
        if (error.message.toLowerCase().includes('password')) {
          return new Error('Password must be at least 9 characters with uppercase, lowercase and special character');
        }
        if (error.message.includes('email')) {
          return new Error('Invalid email address');
        }
        return new Error(error.message || 'An error occurred');
    }
  }
}

export const supabaseAuthService = new SupabaseAuthService();
